import { forwardRef, useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";

// A trailer exists if there's a legacy file or a Bunny upload that hasn't failed.
export const hasTrailer = (film) => Boolean(film?.trailer_path || (film?.bunny_trailer_id && film?.bunny_trailer_status !== "failed"));

const RETRY_MS = 30000;

// <video> that plays Bunny Stream HLS when the backend says the asset is on
// Bunny, otherwise falls back to the legacy progressive URL. With no playable
// source yet (Bunny still encoding, no legacy file) it shows a placeholder and re-checks.
const StreamVideo = forwardRef(function StreamVideo({ playbackPath, legacySrc, onSource, ...videoProps }, ref) {
  const innerRef = useRef(null);
  const [pending, setPending] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const setRefs = (el) => {
    innerRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  };

  useEffect(() => {
    const video = innerRef.current;
    if (!video) return undefined;
    let hls = null;
    let cancelled = false;
    let retry = null;
    const noSource = () => {
      if (cancelled) return;
      if (legacySrc) { setPending(false); video.src = legacySrc; onSource?.("legacy"); return; }
      setPending(true);
      retry = setTimeout(() => setAttempt((a) => a + 1), RETRY_MS);
    };
    api.get(playbackPath).then(({ data }) => {
      if (cancelled) return;
      if (data?.source !== "bunny" || !data.url) return noSource();
      setPending(false);
      onSource?.("bunny");
      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = data.url;
      } else if (Hls.isSupported()) {
        hls = new Hls({ capLevelToPlayerSize: true });
        hls.on(Hls.Events.ERROR, (_e, d) => {
          if (d.fatal && d.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
          else if (d.fatal && d.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
        });
        hls.loadSource(data.url);
        hls.attachMedia(video);
      } else {
        noSource();
      }
    }).catch(noSource);
    return () => { cancelled = true; clearTimeout(retry); hls?.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbackPath, legacySrc, attempt]);

  return (
    <>
      <video ref={setRefs} {...videoProps} controls={pending ? false : videoProps.controls} />
      {pending && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 text-white/75 text-sm pointer-events-none" data-testid="trailer-processing-placeholder">
          <Loader2 className="w-5 h-5 animate-spin text-gold" />
          Trailer processing...
        </div>
      )}
    </>
  );
});

export default StreamVideo;
